'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { usePathname, useRouter } from 'next/navigation';
import { useChat } from '@/hooks/useChat';
import { useAuth } from '@/context/AuthContext';
import {
  HomeIcon,
  SparklesIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  PlusIcon,
  FolderIcon,
  BoltIcon,
  RectangleGroupIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
  Square3Stack3DIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { ButtonV2 } from '@/components/ui/ButtonV2';
import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import Image from 'next/image';
import { useCommandPalette } from '@/context/CommandPaletteContext';
import { NotificationsBell } from '@/components/layout/NotificationsBell';

export const VerticalNavBar: React.FC = () => {
  const pathname = usePathname();
  const router = useRouter();

  // Hide nav on public/auth pages
  if (pathname === '/welcome' || pathname === '/login') return null;
  const { createNewConversation, loadConversation } = useChat();
  const { logOut } = useAuth();
  const { openPalette } = useCommandPalette();
  const [showContentDropdown, setShowContentDropdown] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const buttonRef = React.useRef<HTMLDivElement>(null);
  const [artifactsOpen, setArtifactsOpen] = React.useState(false);

  // Listen for artifacts panel state changes from ChatWindow
  React.useEffect(() => {
    const handleArtifactsChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      // ChatWindow is the source of truth - always use its value
      if (customEvent.detail && typeof customEvent.detail.open === 'boolean') {
        setArtifactsOpen(customEvent.detail.open);
      }
    };

    window.addEventListener('chat:artifacts:state-changed', handleArtifactsChange);
    return () => window.removeEventListener('chat:artifacts:state-changed', handleArtifactsChange);
  }, []); // No dependencies - handler doesn't need to be recreated

  // Determine if we're on a chat page (only the main chat interface)
  const isChatPage = React.useMemo(() => {
    if (!pathname) return false;
    // Only show chat-specific buttons on the main chat page
    return pathname === '/';
  }, [pathname]);

  // Track if component is mounted (for portal)
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setShowContentDropdown(false);
      }
    };
    if (showContentDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showContentDropdown]);

  // Logout handler
  const handleLogout = async () => {
    try {
      await logOut();
      router.push('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const contentItems = [
    { path: '/tools', label: 'PR Tools', icon: DocumentTextIcon },
    { path: '/content', label: 'Content Library', icon: FolderIcon },
    { path: '/workflows', label: 'Workflows', icon: BoltIcon },
    { path: '/workspace', label: 'Workspace', icon: RectangleGroupIcon },
  ];

  const navItems = [
    { icon: HomeIcon, label: 'Chat', href: '/', paths: ['/'] },
    { icon: UserGroupIcon, label: 'Clients', href: '/clients', paths: ['/clients'] },
  ];

  const isActive = (paths: string[]) => {
    return paths.some(path => pathname === path || pathname?.startsWith(path + '/'));
  };

  const isContentActive = contentItems.some(item => pathname === item.path || pathname?.startsWith(item.path + '/'));

  return (
    <div className="h-full w-16 flex-shrink-0 bg-background/95 backdrop-blur-2xl border-r border-border/50 flex flex-col items-center py-6 gap-2 relative overflow-visible">
      <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-pink-500/5 pointer-events-none"></div>
      <div className="relative z-10 w-full h-full flex flex-col items-center gap-2 overflow-y-auto overflow-x-visible">
        {/* Logo */}
        <div className="mb-6 px-1">
          <Link href="/">
            <div className="w-full flex items-center justify-center transition-transform hover:scale-105 cursor-pointer">
              <Image
                src="/micomms-logo.png"
                alt="MiComms"
                width={80}
                height={80}
                className="object-contain w-full h-auto"
                priority
              />
            </div>
          </Link>
        </div>
        <div className="w-8 h-px bg-border/50 mb-2"></div>

        <NotificationsBell />

        {/* Quick Search */}
        <Tooltip label="Quick Search (⌘K)">
          <ButtonV2
            size="icon-lg"
            variant="ghost"
            onClick={openPalette}
            aria-label="Quick Search"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
          </ButtonV2>
        </Tooltip>

        {/* New Chat (chat pages only) */}
        {isChatPage && (
          <Tooltip label="New Chat">
            <ButtonV2
              size="icon-lg"
              variant="primary"
              aria-label="New Chat"
              onClick={async () => {
                try {
                  const id = await createNewConversation('New Conversation');
                  await loadConversation(id);
                } catch {}
              }}
            >
              <PlusIcon className="h-5 w-5" />
            </ButtonV2>
          </Tooltip>
        )}

        <div className="w-8 h-px bg-border/50 my-1"></div>

        {/* Spacer */}
        <div className="h-4"></div>

        {/* Main nav items */}
        {navItems.map((item, index) => (
          <Link key={index} href={item.href}>
            <Tooltip label={item.label}>
              <ButtonV2
                size="icon-lg"
                variant={isActive(item.paths) ? 'active' : 'ghost'}
                aria-label={item.label}
              >
                <item.icon className="h-5 w-5" />
              </ButtonV2>
            </Tooltip>
          </Link>
        ))}

        {/* Content Dropdown */}
        <div className="relative" ref={buttonRef}>
          <Tooltip label="Content">
            <ButtonV2
              size="icon-lg"
              variant={showContentDropdown || isContentActive ? 'active' : 'ghost'}
              onClick={() => setShowContentDropdown(!showContentDropdown)}
              aria-label="Content"
            >
              <FolderIcon className="h-5 w-5" />
            </ButtonV2>
          </Tooltip>
        </div>

        {/* Side dropdown - rendered as portal to escape stacking context */}
        {mounted && showContentDropdown && createPortal(
          <div
            ref={dropdownRef}
            className="fixed left-20 w-56 bg-background border border-border/50 rounded-xl shadow-2xl overflow-hidden"
            style={{
              zIndex: 99999,
              top: 'calc(50vh - 100px)',
              backdropFilter: 'blur(24px)',
              backgroundColor: 'hsl(var(--background) / 0.95)'
            }}
          >
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Content
              </div>
              {contentItems.map((item, index) => (
                <Link
                  key={index}
                  href={item.path}
                  onClick={() => setShowContentDropdown(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-muted/50 transition-colors group"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                  <span className="text-sm text-foreground">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>,
          document.body
        )}

        {/* Chat-specific actions (only on chat pages) */}
        {isChatPage && (
          <>
            {/* Artifacts toggle */}
            <Tooltip label="Artifacts">
              <ButtonV2
                size="icon-lg"
                variant={artifactsOpen ? "active" : "ghost"}
                onClick={() => window.dispatchEvent(new Event('chat:artifacts:toggle'))}
                aria-label="Toggle artifacts"
              >
                <Square3Stack3DIcon className="h-5 w-5" />
              </ButtonV2>
            </Tooltip>
          </>
        )}

        {/* Bottom actions */}
        <div className="mt-auto w-full flex flex-col items-center gap-2 pt-4" data-testid="user-menu">
          {/* Settings moved here above Logout */}
          <Link href="/settings">
            <Tooltip label="Settings">
              <ButtonV2
                size="icon-lg"
                variant="ghost"
                aria-label="Settings"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </ButtonV2>
            </Tooltip>
          </Link>
          {/* New Chat moved to top near Quick Search */}

          {/* Logout button */}
          <Tooltip label="Logout">
            <ButtonV2
              size="icon-lg"
              variant="ghost"
              aria-label="Logout"
              onClick={handleLogout}
            >
              <ArrowLeftOnRectangleIcon className="h-5 w-5" />
            </ButtonV2>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
